import VisitService from '../../services/VisitService';
import OffsiteEstablishmentService from '../../services/OffsiteEstablishmentService';
import BookingRestrictedDateService from '../../services/BookingRestrictedDateService';
import VisitorService from '@/services/VisitorService';
import moment from 'moment';
import Vue from 'vue';

export default {
  namespaced: true,
  state: {
    existingVisits: [],
    offsiteStage: 1,
    offsiteEstablishments: [],
    offsiteVisitors: [], // all visitors currently in offsite establishment
    offsiteBooking: {
      prisonerNumber: '',
      offsite_id: '',
    },
    offsiteContact: {
      offsite_id: '',
      prisoner_firstname: '',
      prisoner_lastname: '',
      prisoner_number: '',
      firstname: '',
      lastname: '',
      relationship: '',
      offsite_identifier: '',
      dob: '',
    },
    manualVisitorEntry: false,
    existingOffsiteVisitor: null, // if the visitor account exists and just need to add a contact
    contacts: [], // available contacts for the offsite booking
    visitor: null, // the contact selected for the booking
    bookingEmail: '', // who to notify about the offsite booking
    selectedBookingSlotDate: null,
    selectedBookingSlotId: null,
    endDateValid: true,
    adHocSlot: null,
    restrictedDates: [],
    dateRestricted: false,
  },
  getters: {
    getKioskEnabled(state) {
      return state.kioskEnabled;
    },
    getKioskItems(state) {
      return state.kioskItems;
    }
  },
  mutations: {
    SET_EXISTING_VISITS(state, value) {
      state.existingVisits = value;
    },
    SET_OFFSITE_BOOKING_ITEM(state, item) {
      state.offsiteBooking[item.prop] = item.value;
    },
    SET_OFFSITE_CONTACT_ITEM(state, item) {
      state.offsiteContact[item.prop] = item.value;
    },
    SET_OFFSITE_CONTACT(state, item) {
      state.offsiteContact = item;
    },
    SET_OFFSITE_ESTABLISHMENTS(state, item) {
      state.offsiteEstablishments = item;
    },
    SET_OFFSITE_STAGE(state, value) {
      state.offsiteStage = value;
    },
    SET_OFFSITE_ESTABLISHMENT_VISITORS(state, item) {
      state.offsiteVisitors = item;
    },
    SET_EXISTING_OFFSITE_VISITOR(state, item) {
      state.existingOffsiteVisitor = item;
    },
    SET_MANUAL_VISITOR_ENTRY(state, item) {
      state.manualVisitorEntry = item;
      state.existingOffsiteVisitor = null; // reset existing visitor
    },
    SET_CONTACTS(state, item) {
      state.contacts = item;
    },
    SET_VISITOR(state, item) {
      state.visitor = item;
    },
    SET_BOOKING_EMAIL(state, item) {
      state.bookingEmail = item;
    },
    SET_SELECTED_BOOKING_SLOT_DATE(state, value) {
      state.selectedBookingSlotDate = value;
    },
    SET_SELECTED_BOOKING_SLOT_ID(state, value) {
      state.selectedBookingSlotId = value;
    },
    SET_ADHOC_SLOT(state, value) {
      state.adHocSlot = value;
    },
    SET_END_DATE_VALID(state, value) {
      state.endDateValid = value;
    },
    SET_RESTRICTED_DATES(state, item) {
      state.restrictedDates = item;
    },
    SET_DATE_RESTRICTED(state, item) {
      state.dateRestricted = item;
    }
  },
  actions: {
    // eslint-disable-next-line no-unused-vars
    checkOverlappingVisitsAwaiting({commit, dispatch}, {prisonerNumber, slotId, date}) {
      return dispatch('getVisitsForPrisoner', prisonerNumber).then((visits) => {
        return visits.find((visit) => {
          let slotMatch = visit.requested_booking_slot_1.booking_slot_id === slotId;
          let existingDate = moment(visit.requested_booking_slot_1.start_date_time).format('YYYY-MM-DD');
          let passedDate = moment(date).format('YYYY-MM-DD');
          let dateMatch = existingDate === passedDate;

          return slotMatch && dateMatch;
        });
      });
    },
    getVisitsForPrisoner({commit}, prisonerNumber) {
      let filters = {
        status: 'AwaitingConfirmation',
        limit: '100',
        skip: 0,
        prisonerNumber: prisonerNumber
      };

      return VisitService.getVisits(filters).then(res => {
        commit('SET_EXISTING_VISITS', res.data.data);
        return res.data.data;
      }).catch(err => {
        console.log('error getting visits', err);
      });
    },
    refreshSites({commit}) {
      OffsiteEstablishmentService.getPaginated({limit: 9999}).then((res) => {
        commit('SET_OFFSITE_ESTABLISHMENTS', res.data.data.map(item => ({
          text: item.name,
          value: item.offsite_id, ...item
        })));
      }).catch(() => {
        Vue.$toast.error('Error getting establishment list, please try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    refreshEstablishmentUsers({commit}, id) {
      VisitorService.getOffsiteVisitorsAtEstablishment(id).then((res) => {
        commit('SET_OFFSITE_ESTABLISHMENT_VISITORS', res.data);
        commit('SET_MANUAL_VISITOR_ENTRY', res.data.length === 0);
      }).catch(() => {
        Vue.$toast.error('Error getting establishment list, please try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    async setBookingEmailToEstablishments({commit, dispatch}) {
      let establishment = await dispatch('getVisitorEstablishment');

      if (establishment) {
        commit('SET_BOOKING_EMAIL', establishment.email);
      }
    },
    getVisitorEstablishment({state}) {
      const Visitor = state.visitor || {user: {offsite_id: null}};
      return state.offsiteEstablishments.find(site => site.offsite_id === Visitor.user.offsite_id);
    },
    resetContact({commit}) {
      commit('SET_OFFSITE_CONTACT', {
        offsite_id: '',
        prisoner_firstname: '',
        prisoner_lastname: '',
        prisoner_number: '',
        firstname: '',
        lastname: '',
        relationship: '',
        offsite_identifier: '',
        dob: '',
      });
    },
    getRestrictedDates({commit}) {
      return BookingRestrictedDateService.getFutureRestrictedDates().then(res => {
        commit('SET_RESTRICTED_DATES', res.data);
        return res.data;
      }).catch(err => {
        console.log('could not get restricted dates', err.response);
      });
    },
    isDateRestricted({state, commit}, dateToCheck) {
      let restricted = false;

      state.restrictedDates.forEach(rDate => {
        const before = moment(dateToCheck).isSameOrBefore(rDate.end_date);
        const after = moment(dateToCheck).isSameOrAfter(rDate.start_date);

        if (before && after) { // if any are true, disallow date
          restricted = true;
        }
      });
      commit('SET_DATE_RESTRICTED', restricted);
      return restricted;
    }
  }
};
